import { useEffect, useState } from "react";
import OfferItem from "../offer-item/OfferItem.component";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";

type SlottedOffer = { slot: number; offer: Offer };

interface Props {
  sessionId: string;
  displayOnly: boolean;
  settings: TemplateOptionSettings;
  campaign: CampaignType;
  offersCount: string;
  offers: Offer[] | SlottedOffer[];
  settingsTemplate: Template;
  campaignId: string;
  placementId: string;
  placementName: string;
  isInline?: boolean;
  isSeen: boolean;
  preEmail: string | undefined;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<void>;
  autoSendingOfferId: string;
  columns?: number | "auto" | string;
  gridTemplateColumns?: string;
  offerCardSize?: "normal" | "large";
  showLogo?: boolean;
}

export const getSectionGridTemplateColumns = (
  columns: number | "auto" | string | undefined
) => {
  if (columns === "auto") {
    return "repeat(auto-fill, minmax(min(100%, 230px, 350px), 1fr))";
  }

  const count = Number(columns) || 3;
  return `repeat(${count}, minmax(0, 1fr))`;
};

const isSlottedOffers = (
  offers: Offer[] | SlottedOffer[]
): offers is SlottedOffer[] =>
  Array.isArray(offers) &&
  offers.length > 0 &&
  typeof (offers[0] as SlottedOffer).slot === "number" &&
  Boolean((offers[0] as SlottedOffer).offer);

const GridOffers = ({
  sessionId,
  isSeen,
  settings,
  preEmail,
  setPreEmail,
  campaign,
  offers,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  openSingle,
  onAdd,
  layoutId,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
  columns,
  gridTemplateColumns,
  offerCardSize,
  showLogo,
}: Props) => {
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);
  const slotted = isSlottedOffers(offers)
    ? offers
    : offers.map((offer, index) => ({ slot: index, offer }));
  const resolvedColumns = columns ?? settings.mainLayout.offersNumber;

  useEffect(() => {
    if (isSeen) {
      const _ids = slotted.map(({ offer, slot }) => ({
        id: offer.id,
        slot: slot + 1,
      }));
      setEventOfferIds(_ids);
    }
    // eslint-disable-next-line
  }, [isSeen]);

  return (
    <div
      className="grid-wrapper grid"
      id="grid-container"
      style={{
        gridTemplateColumns:
          gridTemplateColumns || getSectionGridTemplateColumns(resolvedColumns),
        justifyContent: settings.mainLayout.align,
      }}
    >
      {slotted.map(({ offer, slot }) => (
        <div
          key={offer.id}
          className="preview-selected-offer"
        >
          <OfferItem
            sessionId={sessionId}
            offer={offer}
            slot={slot + 1}
            eventOfferIds={eventOfferIds}
            settings={settings}
            isAdded={offer.isAdded || false}
            settingsTemplate={settingsTemplate}
            onAdd={onAdd}
            displayOnly={displayOnly}
            campaign={campaign}
            retailerId={campaign?.company_Id}
            campaignId={campaignId}
            placementId={placementId}
            placementName={placementName}
            openSingle={openSingle}
            setPreEmail={setPreEmail}
            preEmail={preEmail}
            hasVoucher={slotted.some(
              (item) => item.offer.voucher && item.offer.voucher.voucherType
            )}
            otherSettings={settings.otherSettings}
            layoutId={layoutId}
            layoutOptions={settings.layoutOptions}
            isSeen={isSeen}
            canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
            canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
            onAutoSendEmailFromLabel={onAutoSendEmailFromLabel}
            autoSendingOfferId={autoSendingOfferId === offer.id}
            offerCardSize={offerCardSize}
            showLogo={showLogo}
          />
        </div>
      ))}
    </div>
  );
};

export default GridOffers;
